import castArray from "lodash/castArray";
import {
  MeterValidationResult,
  ValidationFieldResult,
} from "../../../api/meters";

const prefixTabId = (
  prefix: string | number,
  fieldMap: Record<string, string[]>,
) => {
  const BLACKLIST = ["readingDttm"]; // these fields should NOT be prefixed with the tabId
  return Object.entries(fieldMap).reduce(
    (acc, [path, errs]) => ({
      ...acc,
      [BLACKLIST.includes(path) ? path : `${prefix}.${path}`]: errs,
    }),
    {},
  );
};

export const getMessages = (field: ValidationFieldResult): string[] =>
  field.details?.length
    ? field.details.map((d) => d.message)
    : castArray(field.message);

const extractMessagesByFieldname = (
  validationResults: MeterValidationResult,
) => {
  const meterId = validationResults.id;

  const warnings: Record<string, string[]> = {};
  const errors: Record<string, string[]> = {};

  // Meter Fields
  validationResults.fields.forEach((field) => {
    const messages = getMessages(field);

    /* istanbul ignore else */
    if (field.severity === "ERROR") {
      errors[field.name] = messages;
    } else if (field.severity === "WARNING") {
      warnings[field.name] = messages;
    }
  });

  // Meter Registers
  validationResults.registers.forEach((register, i) => {
    register.fields.forEach((field) => {
      const messages = getMessages(field);

      /* istanbul ignore else */
      if (field.severity === "ERROR") {
        errors[`readings[${i}].${field.name}`] = messages;
      } else if (field.severity === "WARNING") {
        warnings[`readings[${i}].${field.name}`] = messages;
      }
    });
  });

  return {
    [meterId]: {
      hasErrors: !!Object.keys(errors).length,
      errors: prefixTabId(meterId, errors),
      hasWarnings: !!Object.keys(warnings).length,
      warnings: prefixTabId(meterId, warnings),
    },
  };
};

export type ValidationResultsByType = {
  hasErrors: boolean;
  errors: { [fieldName: string]: string[] };
  hasWarnings: boolean;
  warnings: { [fieldName: string]: string[] };
};
export type FormattedMeterValidationResults = {
  [tabId: string]: ValidationResultsByType;
};
export const formatMeterValidationResults = (
  validationResults: MeterValidationResult[],
): FormattedMeterValidationResults => {
  return validationResults.reduce((results, validationResult) => {
    return {
      ...results,
      ...extractMessagesByFieldname(validationResult),
    };
  }, {});
};
